/**
 * harness/exporter.js
 * T-Cron-2026-10-22 20:00 (round-4-of-day) — 改进清单 #14「数据:CSV/JSON 导出」
 *
 * 为什么需要:
 *   Vite 树(当前线上权威源)里 **完全没有导出功能**。
 *   `FavoriteBar` 里有一个「确认选材」按钮 —— 但它 **既没有 onClick,
 *   也没有 aria-label,点了什么都不会发生**。这是一个"看起来能用、实际是死的"
 *   控件:用户以为点它会带走选好的素材,结果毫无反应,只能自己一条条复制。
 *
 *   根目录老仓库(components/Header.jsx)有一套成熟实现:
 *   buildCSV / buildJSON / downloadFile / todayStamp,挂在 window 上。
 *   本文件把那套逻辑按 Vite 树的约定重新实现一遍(而不是 import 老仓库的
 *   文件 —— 那是分叉出去的老分支,数据形状不同,详见 README-index.md §⑥)。
 *
 * 数据形状说明(Vite 树特有,与老仓库不同):
 *   · post.versions = { A: {...}, B: {...}, C: {...} }  —— 每个版本自带
 *     title / summary / body / tags / coverImageFile / wordCount / angle
 *   · post.versions[v].coverUrl 由 data/index.js 的 localEnrich 注入
 *     (本地存在封面时 = "assets/covers/<file>",否则 = getBookCover(book) 或 "")
 *   · 老仓库把 coverUrl 放在版本对象上,Vite 树也一样,所以 CSV 列取值路径一致。
 *
 * CSV 规格:
 *   · UTF-8 BOM(\uFEFF)开头 —— 否则 Excel 打开中文全是乱码
 *   · RFC4180 引号转义:含 , " 换行 的字段用双引号包起来,内部 " 写成 ""
 *   · CRLF 行尾 —— Excel 兼容性最好
 *   · **一行一个版本**(不是一行一个选题):老仓库也是这个口径,
 *     选题是容器,版本才是可发布的成品,选材的人要的是成品。
 *
 * JSON 规格:
 *   · 每个选题一个对象,versions 完整保留(不裁剪 body)
 *   · 带 favorite 标记 —— 用户导出的目的多半是"带走我选中的"
 *
 * 挂载约定:和 ContentService / CoverDownload 一样,挂到 window 上,
 * 同时 default export。App.jsx 里通过 window.Exporter 调用。
 */

const EXPORT_COLUMNS = [
  'id', 'date', 'batch', 'book', 'author', 'version',
  'title', 'summary', 'hotTopic', 'tags', 'wordCount', 'coverUrl',
];

/**
 * CSV 单元格转义:含 , " \n \r 的值需要加引号,内部引号翻倍。
 * null / undefined → 空字符串;数组 → 用 | 连接(标签就是数组)。
 */
function escapeCsvCell(value) {
  if (value == null) return '';
  const s = Array.isArray(value) ? value.join('|') : String(value);
  if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
}

/**
 * 构建 CSV 文本。**一行一个版本**。
 * @param {Array} posts
 * @returns {string}
 */
function buildCSV(posts) {
  const lines = [EXPORT_COLUMNS.join(',')];

  for (const post of posts || []) {
    const versions = post.versions || {};
    const versionKeys = Object.keys(versions).sort();

    if (versionKeys.length === 0) {
      // 没有版本的选题(理论上不该出现)——仍然导出一行,字段留空
      lines.push([
        post.id, post.date, post.batch, post.book, post.author, '',
        post.title, post.summary, post.hotTopic, post.tags, post.wordCount, post.coverUrl,
      ].map(escapeCsvCell).join(','));
      continue;
    }

    for (const ver of versionKeys) {
      const vd = versions[ver] || {};
      lines.push([
        post.id,
        post.date,
        post.batch,
        post.book,
        post.author,
        ver,
        vd.title,
        vd.summary,
        post.hotTopic,
        vd.tags || post.tags,
        vd.wordCount,
        vd.coverUrl || post.coverUrl,
      ].map(escapeCsvCell).join(','));
    }
  }

  // BOM 前缀:Excel 识别 UTF-8 CSV 的关键。没有它中文全是乱码。
  return '\uFEFF' + lines.join('\r\n');
}

/**
 * 构建 JSON 文本。每个选题一个对象,versions 完整保留。
 * @param {Array} posts
 * @returns {string}
 */
function buildJSON(posts) {
  const list = (posts || []).map((post) => {
    const versions = {};
    for (const [key, val] of Object.entries(post.versions || {})) {
      versions[key] = val;
    }
    return {
      id: post.id,
      date: post.date,
      batch: post.batch,
      book: post.book,
      author: post.author,
      category: post.category,
      hotTopic: post.hotTopic,
      tags: post.tags || [],
      coverUrl: post.coverUrl,
      versions,
      favorite: !!post.favorite,
    };
  });
  return JSON.stringify(list, null, 2);
}

/**
 * 触发浏览器下载。
 * @param {string} filename
 * @param {string} content
 * @param {string} mime
 * @returns {boolean} 是否成功触发
 */
function downloadFile(filename, content, mime) {
  try {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // 延后 revoke —— iOS Safari 需要时间真正开始下载,立刻 revoke 会失败。
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * 今天的日期戳 YYYYMMDD,用于文件名。
 * @returns {string}
 */
function todayStamp() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

/**
 * 导出入口。scope 决定导出范围:
 *   'favorites' → 只导出已收藏
 *   'visible'   → 导出当前筛选后可见的
 * @param {Array} posts 待导出列表
 * @param {'csv'|'json'} format
 * @param {string} scope
 * @returns {{ok: boolean, filename: string, rows: number, message: string}}
 */
function exportPosts(posts, format, scope) {
  const list = posts || [];
  if (list.length === 0) {
    return { ok: false, filename: '', rows: 0, message: '没有可导出的内容' };
  }

  const isJson = format === 'json';
  const content = isJson ? buildJSON(list) : buildCSV(list);
  // scopeTag 决定文件名里的范围标记:
  //   'favorites' → 'favorites-'  (已选素材)
  //   'visible'   → 'visible-'    (当前筛选后可见的)
  //   其它/未传   → ''            (全量)
  // T-Cron-2026-10-23 03:30:之前只有 favorites 一种 scope,visible 会退化成
  // 无标记文件名,和「全量导出」撞名 —— 用户在下载列表里分不清是哪次导出。
  const scopeTag = scope === 'favorites' ? 'favorites-' : (scope === 'visible' ? 'visible-' : '');
  const filename = `xhs-kanban-${scopeTag}${todayStamp()}.${isJson ? 'json' : 'csv'}`;
  const mime = isJson ? 'application/json;charset=utf-8' : 'text/csv;charset=utf-8';

  const ok = downloadFile(filename, content, mime);
  if (!ok) {
    return { ok: false, filename, rows: 0, message: '导出失败,浏览器不支持下载' };
  }

  // 行数口径:CSV 是"一行一个版本",JSON 是"一个对象一个选题"。
  const rows = isJson
    ? list.length
    : list.reduce((acc, p) => acc + Math.max(1, Object.keys(p.versions || {}).length), 0);

  return {
    ok: true,
    filename,
    rows,
    message: isJson ? `已导出 ${list.length} 条 JSON` : `已导出 ${rows} 条 CSV`,
  };
}

const Exporter = {
  EXPORT_COLUMNS,
  buildCSV,
  buildJSON,
  downloadFile,
  todayStamp,
  exportPosts,
};

if (typeof window !== 'undefined') {
  window.Exporter = Exporter;
  // 与老仓库一致的单函数别名,方便调试 / console 调用
  window.buildCSV = buildCSV;
  window.buildJSON = buildJSON;
  window.downloadFile = downloadFile;
  window.todayStamp = todayStamp;
}

export default Exporter;
